import type { AccountTab } from "./portal-navigation";

type Resource = { id: string; label?: string; status?: string };
export type AccountSummaryData = {
  account: { kind: string; billing_mode?: string };
  numbers: (Resource & { e164: string })[];
  devices: (Resource & { model: string })[];
  lines: Resource[];
  locations: (Resource & { name: string })[];
  subscriptions: (Resource & { plan_name: string; recurring_minor: number })[];
  numberCharges?: {
    numberId: string;
    e164: string;
    monthlyMinor: number;
    setupMinor: number;
    currency: "USD";
    pricingReason: "included" | "additional" | "complimentary";
    effectiveAt: string;
    paymentCollected: false;
  }[];
};

export function AccountServiceSummary({
  data,
  customer,
  readOnly = false,
  onTab,
}: {
  data: AccountSummaryData;
  customer: boolean;
  readOnly?: boolean;
  onTab: (tab: AccountTab) => void;
}) {
  const lines = data.lines.filter((line) => line.status !== "disconnected");
  const entries: {
    title: string;
    detail: string;
    action: string;
    tab: AccountTab;
  }[] = [
    {
      title: "Telephone numbers",
      detail: data.numbers.length
        ? data.numbers.map((number) => number.e164).join(", ")
        : "No number assigned to this account yet.",
      action: readOnly
        ? "Review numbers"
        : data.numbers.length
          ? "View numbers"
          : customer
            ? "Choose or transfer a number"
            : "Assign a number",
      tab: "numbers",
    },
    {
      title: "Equipment",
      detail: data.devices.length
        ? data.devices
            .map((device) =>
              device.label ? `${device.label} (${device.model})` : device.model,
            )
            .join(", ")
        : "No phone or adapter registered yet.",
      action: readOnly
        ? "View equipment"
        : data.devices.length
          ? "Equipment and setup details"
          : "Add equipment",
      tab: "devices",
    },
    {
      title:
        data.account.kind !== "residential"
          ? "Extensions and lines"
          : "Telephone line",
      detail: lines.length
        ? lines.map((line) => line.label || "Telephone line").join(", ")
        : "No telephone line is recorded yet." +
          (data.numbers.length || data.devices.length
            ? " Your existing numbers and equipment are listed above."
            : ""),
      action:
        customer || readOnly
          ? "View service setup"
          : data.account.kind !== "residential"
            ? "Manage extensions"
            : "Set up telephone line",
      tab:
        !customer && !readOnly && data.account.kind !== "residential"
          ? "users"
          : "services",
    },
  ];
  return (
    <section
      className="cr-panel cr-wide cr-account-summary"
      aria-label="Telephone service summary"
    >
      <h3>{customer ? "Your telephone service" : "Telephone service"}</h3>
      <ul className="cr-account-resource-list">
        {entries.map((entry) => (
          <li key={entry.title}>
            <div>
              <strong>{entry.title}</strong>
              <p>{entry.detail}</p>
            </div>
            <button
              className="cr-button secondary"
              onClick={() => onTab(entry.tab)}
            >
              {entry.action}
            </button>
          </li>
        ))}
      </ul>
      <p className="cr-form-note">
        Account records are shown here. Open service setup for connection
        details; a saved number or device does not confirm working calls.
      </p>
    </section>
  );
}

export function AccountBillingSummary({ data }: { data: AccountSummaryData }) {
  const complimentary = data.account.billing_mode === "complimentary";
  const numberCharges = data.numberCharges ?? [];
  const currency = (minor: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(minor / 100);
  return (
    <section className="cr-panel" aria-label="Account billing summary">
      <h3>{complimentary ? "Complimentary service" : "Service charges"}</h3>
      {complimentary ? (
        <p>
          <strong>$0 per month</strong> for this {data.account.kind} account. No
          recurring service charge.
        </p>
      ) : data.subscriptions.length ? (
        <dl className="cr-details">
          {data.subscriptions.map((item) => (
            <div key={item.id}>
              <dt>{item.plan_name}</dt>
              <dd>
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(item.recurring_minor / 100)}{" "}
                recurring · {item.status?.replaceAll("_", " ")}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p>
          No billing subscription has been created. A selected setup plan does
          not start billing.
        </p>
      )}
      {numberCharges.length > 0 && (
        <>
          <h4>Telephone numbers</h4>
          <dl className="cr-details">
            {numberCharges.map((charge) => (
              <div key={charge.numberId}>
                <dt>{charge.e164}</dt>
                <dd>
                  {complimentary || charge.pricingReason === "complimentary"
                    ? "Included with complimentary service"
                    : charge.pricingReason === "included"
                      ? "First number included in your monthly plan"
                      : `${currency(charge.monthlyMinor)} per month at Twilio’s cost`}
                </dd>
              </div>
            ))}
          </dl>
          <p className="cr-form-note">
            These are the terms accepted when each number was purchased. There
            is no setup fee. A purchase record is not a payment receipt.
          </p>
        </>
      )}
    </section>
  );
}

export function setupNumberMessage(
  request: {
    mode: string;
    number?: string;
    carrier?: string;
    areaCode?: string;
  },
  numbers: { e164: string }[],
) {
  const normalized = (value: string) =>
    value.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  const assigned =
    request.number &&
    numbers.some(
      (number) => normalized(number.e164) === normalized(request.number!),
    );
  if (assigned)
    return `${request.number} is assigned to this account. See Numbers for its current setup status.`;
  if (request.mode === "existing")
    return `Requested number: ${request.number}${request.carrier ? ` with ${request.carrier}` : ""}. See Numbers to review the transfer or assign it from existing inventory.`;
  if (request.mode === "new")
    return request.number
      ? `Requested new number: ${request.number}. It has not been assigned to this account.`
      : `A new number is requested${request.areaCode ? ` in area code ${request.areaCode}` : " near your service address"}. Staff will confirm available choices; none is assigned yet.`;
  return numbers.length
    ? "Telephone numbers are assigned to this account. See Numbers for their current setup status."
    : "Telephone number will be chosen later.";
}
